import { memo } from "react";

import type { Attribute, SharedFieldProps } from "../../types";
import { MultiSelectCheckboxField } from "../MultiSelectCheckboxField/MultiSelectCheckboxField";

import { ChangeCheckboxField } from "./ChangeCheckboxField";
import { ChangeNumberField } from "./ChangeNumberField";
import { ChangeRadioField } from "./ChangeRadioField";
import { ChangeSingleSelectField } from "./ChangeSingleSelectField";
import { ChangeTextField } from "./ChangeTextField";
import { CheckboxField } from "./CheckboxField";
import { DateField } from "./DateField";
import { DynamicSelectField } from "./DynamicField";
import { EmailField } from "./EmailField";
import {
  EquipmentProvidableSelectField,
  EquipmentSelectField,
} from "./EquipmentSelectField";
import { FileField } from "./FileField";
import { HiddenField } from "./HiddenField";
import { HighlyConfidentialTextField } from "./HighlyConfidentialTextField";
import { NumberField } from "./NumberField";
import { PersonSelectField } from "./PersonSelectField";
import { PhoneField } from "./PhoneField";
import { PlaceSelectField } from "./PlaceSelectField";
import { RadioField } from "./RadioField";
import { SelectField } from "./SelectField";
import { SoftwareSelectField } from "./SoftwareSelectField";
import { TextAreaField } from "./TextAreaField";
import { TextField } from "./TextField";

/**
 * フォームフィールドのプロパティ
 */
interface FormFieldProps extends SharedFieldProps {
  /** 属性定義 */
  attribute?: Attribute;
  /** フォーム内のすべてのフィールド値（動的フィールドの依存関係解決に使用） */
  allValues?: import("../../types").FieldValue[];
}

const FIELD_COMPONENTS = {
  Text: TextField,
  TextArea: TextAreaField,
  Date: DateField,
  Email: EmailField,
  Tel: PhoneField,
  Integer: NumberField,
  ChangeInteger: ChangeNumberField,
  Decimal: NumberField,
  ChangeDecimal: ChangeNumberField,
  Boolean: CheckboxField,
  SingleSelect: SelectField,
  MultiSelectCheckbox: MultiSelectCheckboxField,
  Radio: RadioField,
  ChangeRadio: ChangeRadioField,
  Password: TextField,
  Url: TextField,
  HighlyConfidentialText: HighlyConfidentialTextField,
  Hidden: HiddenField,
  Person: PersonSelectField,
  Software: SoftwareSelectField,
  Place: PlaceSelectField,
  Equipment: EquipmentSelectField,
  EquipmentProvidable: EquipmentProvidableSelectField,
  File: FileField,
  ChangeText: ChangeTextField,
  ChangeSingleSelect: ChangeSingleSelectField,
  ChangeBoolean: ChangeCheckboxField,
} as const;

/**
 * フォームフィールドコンポーネント
 * フィールドタイプに基づいて適切なフィールドコンポーネントをレンダリング
 */
export const FormField = memo(function FormField(props: FormFieldProps) {
  const { attribute, allValues } = props;

  if (!attribute) {
    return null;
  }

  // 動的セレクトフィールドの場合は適切なDynamicフィールドを使用
  if (
    attribute.isDynamic &&
    attribute.dependencyInfo &&
    (attribute.type === "SingleSelect" ||
      attribute.type === "ChangeSingleSelect")
  ) {
    return (
      <DynamicSelectField
        {...props}
        type={attribute.type}
        field={attribute}
        sourceFieldCode={attribute.dependencyInfo.sourceField}
        targetFieldCode={attribute.id}
        allValues={allValues}
        className="w-full gap-1"
      />
    );
  }

  // フィールドタイプに対応するコンポーネントを選択
  // QrCodeタイプはHiddenFieldとして扱う
  const FieldComponent =
    attribute.type === "QrCode"
      ? FIELD_COMPONENTS.Hidden
      : FIELD_COMPONENTS[attribute.type] || TextField;
  // 選択したコンポーネントをレンダリング
  return (
    <FieldComponent {...props} field={attribute} className="w-full gap-1" />
  );
});

FormField.displayName = "FormField";
